import { useState, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import {
  Chart as ChartJS,
  ArcElement,
  BarElement,
  LineElement,
  PointElement,
  CategoryScale,
  LinearScale,
  Filler,
  Tooltip,
  Legend,
} from 'chart.js'
import { Doughnut, Bar, Line } from 'react-chartjs-2'
import { sourcesApi, scansApi, cbomApi, inventoryApi } from '@/api/clients/apiClient'
import { useOrgStore } from '@/stores/orgStore'
import { useTheme } from '@/contexts/ThemeContext'
import { ScanStatusBadge, ModalityBadge } from '@/components/common/StatusBadge'
import { ProgressBar } from '@/components/common/ProgressBar'
import { Card } from '@/components/common/ui'
import { Spinner, EmptyState } from '@/components/common/State'
import type { ScanJob, AssetType, FindingsReport, PackageFindingsReport, Modality } from '@/api/types'

// Design-token colors for Chart.js (not aware of Tailwind/CSS vars)
function chartTokens(isDark: boolean) {
  return {
    primary:    isDark ? '#94C8E4' : '#254F71',
    primaryBg:  isDark ? 'rgba(148,200,228,0.08)' : 'rgba(37,79,113,0.07)',
    success:    '#22c55e',
    successBg:  'rgba(34,197,94,0.12)',
    error:      '#C22221',
    errorBg:    'rgba(194,34,33,0.08)',
    warning:    '#eab308',
    neutral:    isDark ? 'rgba(122,154,184,0.55)' : 'rgba(77,96,112,0.60)',
    textSubtle: isDark ? '#7a9ab8' : '#4d6070',
    textMain:   isDark ? '#e8ecf2' : '#0F151B',
    gridLine:   isDark ? 'rgba(30,44,62,0.8)' : 'rgba(221,227,235,0.7)',
  }
}

ChartJS.register(ArcElement, BarElement, LineElement, PointElement, CategoryScale, LinearScale, Filler, Tooltip, Legend)

// ── KPI stat card ─────────────────────────────────────────────────────────────

function KpiCard({
  label,
  value,
  icon,
  color = 'text-text-light dark:text-text-dark',
  iconBg = 'bg-primary/10 dark:bg-primary/15',
  sub,
  trend,
}: {
  label: string
  value: string | number
  icon: string
  color?: string
  iconBg?: string
  sub?: string
  trend?: { delta: number; label: string }
}) {
  return (
    <div className="bg-surface-light dark:bg-surface-dark rounded-2xl border border-border-light dark:border-border-dark p-5">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="label-sm text-subtle-light dark:text-subtle-dark">{label}</p>
          <p className={`text-3xl font-bold mt-1.5 tracking-display ${color}`}>{value}</p>
          {sub && <p className="text-xs text-subtle-light dark:text-subtle-dark mt-1">{sub}</p>}
          {trend && (
            <p className={`text-xs mt-1 ${trend.delta > 0 ? 'text-success' : 'text-subtle-light dark:text-subtle-dark'}`}>
              {trend.delta > 0 ? '↑' : trend.delta < 0 ? '↓' : '–'} {trend.delta > 0 ? '+' : ''}{trend.delta} {trend.label}
            </p>
          )}
        </div>
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${iconBg}`}>
          <span className={`material-icons text-xl ${color}`}>{icon}</span>
        </div>
      </div>
    </div>
  )
}

function formatRelativeTime(iso?: string) {
  if (!iso) return '—'
  const diff = Date.now() - new Date(iso).getTime()
  const mins = Math.floor(diff / 60000)
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins}m ago`
  const hrs = Math.floor(mins / 60)
  if (hrs < 24) return `${hrs}h ago`
  return `${Math.floor(hrs / 24)}d ago`
}

// ── Scan timeline ─────────────────────────────────────────────────────────────

const DURATION_OPTIONS = [
  { label: '1h', ms: 60 * 60 * 1000 },
  { label: '6h', ms: 6 * 60 * 60 * 1000 },
  { label: '24h', ms: 24 * 60 * 60 * 1000 },
  { label: '7d', ms: 7 * 24 * 60 * 60 * 1000 },
]

const BUCKET_CONFIG = [
  { count: 12, labelFn: (ts: number) => new Date(ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) },
  { count: 12, labelFn: (ts: number) => new Date(ts).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) },
  { count: 12, labelFn: (ts: number) => new Date(ts).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) },
  { count:  7, labelFn: (ts: number) => new Date(ts).toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' }) },
]

function ScanActivityChart({ scans, isDark }: { scans: ScanJob[]; isDark: boolean }) {
  const [durationIdx, setDurationIdx] = useState(2)
  const { ms: duration, label: durationLabel } = DURATION_OPTIONS[durationIdx]
  const { count: numBuckets, labelFn } = BUCKET_CONFIG[durationIdx]
  const now = Date.now()
  const windowStart = now - duration
  const bucketMs = duration / numBuckets
  const tok = chartTokens(isDark)

  const counts = {
    pending:   Array<number>(numBuckets).fill(0),
    running:   Array<number>(numBuckets).fill(0),
    completed: Array<number>(numBuckets).fill(0),
    failed:    Array<number>(numBuckets).fill(0),
  }
  scans.forEach((s) => {
    const ts = new Date(s.started_at ?? s.created_at).getTime()
    if (ts < windowStart || ts > now) return
    if (!(s.status in counts)) return
    const idx = Math.min(Math.floor((ts - windowStart) / bucketMs), numBuckets - 1)
    counts[s.status][idx]++
  })

  const labels = Array.from({ length: numBuckets }, (_, i) => labelFn(windowStart + i * bucketMs))

  const chartData = {
    labels,
    datasets: [
      { label: 'Completed', data: counts.completed, backgroundColor: tok.success, stack: 'a' },
      { label: 'Running',   data: counts.running,   backgroundColor: tok.primary, stack: 'a' },
      { label: 'Pending',   data: counts.pending,   backgroundColor: tok.neutral, stack: 'a' },
      { label: 'Failed',    data: counts.failed,    backgroundColor: tok.error,   stack: 'a' },
    ],
  }

  const hasActivity = scans.some((s) => {
    const ts = new Date(s.started_at ?? s.created_at).getTime()
    return ts >= windowStart && ts <= now
  })

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: 'bottom' as const, labels: { color: tok.textMain, boxWidth: 12, padding: 16, font: { size: 11 } } },
      tooltip: { mode: 'index' as const, intersect: false },
    },
    scales: {
      x: { stacked: true, grid: { display: false }, ticks: { color: tok.textSubtle, maxRotation: 45, font: { size: 10 } } },
      y: { stacked: true, beginAtZero: true, grid: { color: tok.gridLine }, ticks: { color: tok.textSubtle, stepSize: 1, precision: 0, font: { size: 10 } } },
    },
  }

  return (
    <div>
      <div className="flex gap-1 mb-4">
        {DURATION_OPTIONS.map((opt, i) => (
          <button
            key={opt.label}
            onClick={() => setDurationIdx(i)}
            className={`px-3 py-1 text-xs rounded-md font-medium transition-colors ${
              i === durationIdx
                ? 'bg-primary text-white'
                : 'bg-container-light dark:bg-container-dark text-subtle-light dark:text-subtle-dark hover:text-text-light dark:hover:text-text-dark'
            }`}
          >
            {opt.label}
          </button>
        ))}
      </div>
      {!hasActivity ? (
        <EmptyState icon="bar_chart" title={`No scan activity in the last ${durationLabel}`} />
      ) : (
        <div className="h-52">
          <Bar data={chartData} options={chartOptions} />
        </div>
      )}
    </div>
  )
}

// ── Asset trend chart ─────────────────────────────────────────────────────────

const TREND_WINDOWS = [
  { label: '7d',  ms: 7  * 86_400_000 },
  { label: '30d', ms: 30 * 86_400_000 },
  { label: 'All', ms: Infinity },
]

function buildTrendPoints(
  data: { scan: ScanJob; findings: FindingsReport | null }[],
  windowMs: number
): { date: Date; total: number; quantumSafe: number; quantumRisk: number; bucketMs: number }[] {
  const withFindings = data.filter(({ findings }) => findings !== null)
  if (withFindings.length === 0) return []

  const sorted = [...withFindings].sort(
    (a, b) =>
      new Date(a.scan.completed_at ?? a.scan.created_at).getTime() -
      new Date(b.scan.completed_at ?? b.scan.created_at).getTime()
  )

  const now = Date.now()
  const firstScanTs = new Date(sorted[0].scan.completed_at ?? sorted[0].scan.created_at).getTime()
  const windowStart = windowMs === Infinity ? firstScanTs : Math.max(now - windowMs, firstScanTs)
  const range = now - windowStart

  let bucketMs: number
  if (range <= 2 * 86_400_000) bucketMs = 60 * 60_000
  else if (range <= 35 * 86_400_000) bucketMs = 86_400_000
  else if (range <= 90 * 86_400_000) bucketMs = 7 * 86_400_000
  else bucketMs = 30 * 86_400_000

  const numBuckets = Math.ceil(range / bucketMs)
  const points: { date: Date; total: number; quantumSafe: number; quantumRisk: number; bucketMs: number }[] = []
  const bucketOffset = Math.floor(now / bucketMs) * bucketMs

  for (let i = 0; i < numBuckets; i++) {
    const bucketEnd = bucketOffset - (numBuckets - 1 - i) * bucketMs + bucketMs
    const sourceState = new Map<string, { total: number; quantumSafe: number; quantumRisk: number }>()
    for (const { scan, findings } of sorted) {
      const ts = new Date(scan.completed_at ?? scan.created_at).getTime()
      if (ts > bucketEnd) break
      sourceState.set(scan.source_id, {
        total: findings!.summary.total,
        quantumSafe: findings!.summary.quantum_safe,
        quantumRisk: findings!.summary.quantum_unsafe,
      })
    }
    if (sourceState.size === 0) continue
    let total = 0, quantumSafe = 0, quantumRisk = 0
    for (const v of sourceState.values()) {
      total += v.total
      quantumSafe += v.quantumSafe
      quantumRisk += v.quantumRisk
    }
    points.push({ date: new Date(Math.min(bucketEnd, now)), total, quantumSafe, quantumRisk, bucketMs })
  }

  return points
}

function AssetTrendChart({
  data,
  isLoading,
  isDark,
  sourceCap,
}: {
  data: { scan: ScanJob; findings: FindingsReport | null }[]
  isLoading: boolean
  isDark: boolean
  sourceCap?: number
}) {
  const [windowIdx, setWindowIdx] = useState(1)
  const windowMs = TREND_WINDOWS[windowIdx].ms
  const visible = buildTrendPoints(data, windowMs)
  const tok = chartTokens(isDark)

  if (isLoading) return <Spinner />

  if (visible.length === 0) {
    return <EmptyState icon="show_chart" title="No completed scans in this window" />
  }

  const bucketMs = visible[0]?.bucketMs ?? 86_400_000
  const labels = visible.map((p) => {
    if (bucketMs <= 60 * 60_000)
      return p.date.toLocaleDateString([], { month: 'short', day: 'numeric' }) +
             ' ' + p.date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    return p.date.toLocaleDateString([], { month: 'short', day: 'numeric' })
  })

  const noPoints = visible.length > 20
  const chartData = {
    labels,
    datasets: [
      { label: 'Total Assets', data: visible.map((p) => p.total), borderColor: tok.primary, backgroundColor: tok.primaryBg, borderWidth: 2, tension: 0.3, pointRadius: noPoints ? 0 : 3, fill: false },
      { label: 'Quantum Safe', data: visible.map((p) => p.quantumSafe), borderColor: tok.success, backgroundColor: tok.successBg, borderWidth: 2, tension: 0.3, pointRadius: noPoints ? 0 : 3, fill: true },
      { label: 'Quantum Risk', data: visible.map((p) => p.quantumRisk), borderColor: tok.error, backgroundColor: tok.errorBg, borderWidth: 2, tension: 0.3, pointRadius: noPoints ? 0 : 3, fill: true },
    ],
  }

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: 'bottom' as const, labels: { color: tok.textMain, boxWidth: 12, padding: 16, font: { size: 11 } } },
      tooltip: { mode: 'index' as const, intersect: false },
    },
    scales: {
      x: { grid: { display: false }, ticks: { color: tok.textSubtle, maxRotation: 45, maxTicksLimit: 12, font: { size: 10 } } },
      y: { beginAtZero: true, grid: { color: tok.gridLine }, ticks: { color: tok.textSubtle, precision: 0, font: { size: 10 } } },
    },
  }

  return (
    <div>
      <div className="flex items-center gap-3 mb-4">
        <div className="flex gap-1">
          {TREND_WINDOWS.map((w, i) => (
            <button
              key={w.label}
              onClick={() => setWindowIdx(i)}
              className={`px-3 py-1 text-xs rounded-md font-medium transition-colors ${
                i === windowIdx
                  ? 'bg-primary text-white'
                  : 'bg-container-light dark:bg-container-dark text-subtle-light dark:text-subtle-dark hover:text-text-light dark:hover:text-text-dark'
              }`}
            >
              {w.label}
            </button>
          ))}
        </div>
        {sourceCap != null && (
          <span className="text-xs text-subtle-light dark:text-subtle-dark">top {sourceCap} sources</span>
        )}
      </div>
      <div className="h-52">
        <Line data={chartData} options={chartOptions} />
      </div>
    </div>
  )
}

// ── Package trend chart ───────────────────────────────────────────────────────

function buildPkgTrendPoints(
  data: { scan: ScanJob; findings: PackageFindingsReport | null }[],
  windowMs: number
): { date: Date; total: number; quantumSafe: number; quantumRisk: number; bucketMs: number }[] {
  const withFindings = data.filter(({ findings }) => findings !== null)
  if (withFindings.length === 0) return []

  const sorted = [...withFindings].sort(
    (a, b) =>
      new Date(a.scan.completed_at ?? a.scan.created_at).getTime() -
      new Date(b.scan.completed_at ?? b.scan.created_at).getTime()
  )

  const now = Date.now()
  const firstScanTs = new Date(sorted[0].scan.completed_at ?? sorted[0].scan.created_at).getTime()
  const windowStart = windowMs === Infinity ? firstScanTs : Math.max(now - windowMs, firstScanTs)
  const range = now - windowStart

  let bucketMs: number
  if (range <= 2 * 86_400_000) bucketMs = 60 * 60_000
  else if (range <= 35 * 86_400_000) bucketMs = 86_400_000
  else if (range <= 90 * 86_400_000) bucketMs = 7 * 86_400_000
  else bucketMs = 30 * 86_400_000

  const numBuckets = Math.ceil(range / bucketMs)
  const points: { date: Date; total: number; quantumSafe: number; quantumRisk: number; bucketMs: number }[] = []
  const bucketOffset = Math.floor(now / bucketMs) * bucketMs

  for (let i = 0; i < numBuckets; i++) {
    const bucketEnd = bucketOffset - (numBuckets - 1 - i) * bucketMs + bucketMs
    const sourceState = new Map<string, { total: number; quantumSafe: number; quantumRisk: number }>()
    for (const { scan, findings } of sorted) {
      const ts = new Date(scan.completed_at ?? scan.created_at).getTime()
      if (ts > bucketEnd) break
      const total = findings!.summary.total
      const quantumSafe = findings!.summary.by_quantum_risk?.['safe'] ?? 0
      sourceState.set(scan.source_id, { total, quantumSafe, quantumRisk: total - quantumSafe })
    }
    if (sourceState.size === 0) continue
    let total = 0, quantumSafe = 0, quantumRisk = 0
    for (const v of sourceState.values()) {
      total += v.total
      quantumSafe += v.quantumSafe
      quantumRisk += v.quantumRisk
    }
    points.push({ date: new Date(Math.min(bucketEnd, now)), total, quantumSafe, quantumRisk, bucketMs })
  }

  return points
}

function PackageTrendChart({
  data,
  isLoading,
  isDark,
  sourceCap,
}: {
  data: { scan: ScanJob; findings: PackageFindingsReport | null }[]
  isLoading: boolean
  isDark: boolean
  sourceCap?: number
}) {
  const [windowIdx, setWindowIdx] = useState(1)
  const windowMs = TREND_WINDOWS[windowIdx].ms
  const visible = buildPkgTrendPoints(data, windowMs)
  const tok = chartTokens(isDark)

  if (isLoading) return <Spinner />

  if (visible.length === 0) {
    return <EmptyState icon="show_chart" title="No completed scans in this window" />
  }

  const bucketMs = visible[0]?.bucketMs ?? 86_400_000
  const labels = visible.map((p) => {
    if (bucketMs <= 60 * 60_000)
      return p.date.toLocaleDateString([], { month: 'short', day: 'numeric' }) +
             ' ' + p.date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    return p.date.toLocaleDateString([], { month: 'short', day: 'numeric' })
  })

  const noPoints = visible.length > 20
  const chartData = {
    labels,
    datasets: [
      { label: 'Total Packages', data: visible.map((p) => p.total), borderColor: tok.primary, backgroundColor: tok.primaryBg, borderWidth: 2, tension: 0.3, pointRadius: noPoints ? 0 : 3, fill: false },
      { label: 'Quantum Safe',   data: visible.map((p) => p.quantumSafe), borderColor: tok.success, backgroundColor: tok.successBg, borderWidth: 2, tension: 0.3, pointRadius: noPoints ? 0 : 3, fill: true },
      { label: 'Quantum Risk',   data: visible.map((p) => p.quantumRisk), borderColor: tok.error, backgroundColor: tok.errorBg, borderWidth: 2, tension: 0.3, pointRadius: noPoints ? 0 : 3, fill: true },
    ],
  }

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: 'bottom' as const, labels: { color: tok.textMain, boxWidth: 12, padding: 16, font: { size: 11 } } },
      tooltip: { mode: 'index' as const, intersect: false },
    },
    scales: {
      x: { grid: { display: false }, ticks: { color: tok.textSubtle, maxRotation: 45, maxTicksLimit: 12, font: { size: 10 } } },
      y: { beginAtZero: true, grid: { color: tok.gridLine }, ticks: { color: tok.textSubtle, precision: 0, font: { size: 10 } } },
    },
  }

  return (
    <div>
      <div className="flex items-center gap-3 mb-4">
        <div className="flex gap-1">
          {TREND_WINDOWS.map((w, i) => (
            <button
              key={w.label}
              onClick={() => setWindowIdx(i)}
              className={`px-3 py-1 text-xs rounded-md font-medium transition-colors ${
                i === windowIdx
                  ? 'bg-primary text-white'
                  : 'bg-container-light dark:bg-container-dark text-subtle-light dark:text-subtle-dark hover:text-text-light dark:hover:text-text-dark'
              }`}
            >
              {w.label}
            </button>
          ))}
        </div>
        {sourceCap != null && (
          <span className="text-xs text-subtle-light dark:text-subtle-dark">top {sourceCap} sources</span>
        )}
      </div>
      <div className="h-52">
        <Line data={chartData} options={chartOptions} />
      </div>
    </div>
  )
}

// ── Dashboard page ────────────────────────────────────────────────────────────

const ASSET_SOURCE_CAP = 50
const TREND_SOURCE_CAP = 20

export default function Dashboard() {
  const { currentOrgId, orgName } = useOrgStore()
  const { theme } = useTheme()
  const isDark = theme === 'dark'

  const { data: sourcesResp, isLoading: loadingSources, isError: sourcesError } = useQuery({
    queryKey: ['sources', currentOrgId, ASSET_SOURCE_CAP],
    queryFn: () => sourcesApi.list(currentOrgId, { limit: ASSET_SOURCE_CAP }),
  })

  const { data: scansResp, isLoading: loadingScans, isError: scansError, error: scansErr } = useQuery({
    queryKey: ['scans', currentOrgId, 'recent'],
    queryFn: () => scansApi.list(currentOrgId, { limit: 50 }),
    refetchInterval: (query) => {
      const scans = query.state.data?.data ?? []
      return scans.some((s) => s.status === 'running' || s.status === 'pending') ? 5000 : false
    },
  })

  const sources = sourcesResp?.data ?? []
  const sourcesCapped = sources.length === ASSET_SOURCE_CAP
  const scans = scansResp?.data ?? []
  const sourceIds = sources.map((s) => s.id)
  const trendSourceIds = sourceIds.slice(0, TREND_SOURCE_CAP)
  const trendSourcesCapped = sourceIds.length > TREND_SOURCE_CAP

  const assetSummaryQuery = useQuery({
    queryKey: ['asset-summary-per-source', currentOrgId, sourceIds],
    queryFn: async () => {
      if (sourceIds.length === 0) return []
      const latestScans = await Promise.all(
        sourceIds.map((sourceId) =>
          scansApi
            .list(currentOrgId, { source_id: sourceId, status: 'completed', limit: 10 })
            .then((resp) => {
              const completed = resp.data ?? []
              if (completed.length === 0) return null
              return completed.reduce((a, b) => {
                const ta = new Date(a.completed_at ?? a.created_at).getTime()
                const tb = new Date(b.completed_at ?? b.created_at).getTime()
                return tb > ta ? b : a
              })
            })
            .catch(() => null)
        )
      )
      const scanIds = latestScans.filter((s): s is ScanJob => s !== null).map((s) => s.id)
      if (scanIds.length === 0) return []
      return Promise.all(
        scanIds.map((scanId) => cbomApi.fetchScanFindings(currentOrgId, scanId).catch(() => null))
      )
    },
    enabled: sourceIds.length > 0,
    retry: false,
  })

  const trendQuery = useQuery({
    queryKey: ['asset-trend', currentOrgId, trendSourceIds],
    queryFn: async () => {
      if (trendSourceIds.length === 0) return []
      const perSourceScans = await Promise.all(
        trendSourceIds.map((sid) =>
          scansApi
            .list(currentOrgId, { source_id: sid, status: 'completed', limit: 100 })
            .then((r) => r.data ?? [])
            .catch(() => [] as ScanJob[])
        )
      )
      const dayMap = new Map<string, ScanJob>()
      for (const scan of perSourceScans.flat()) {
        const day = (scan.completed_at ?? scan.created_at).slice(0, 10)
        const key = `${scan.source_id}::${day}`
        const prev = dayMap.get(key)
        if (!prev) {
          dayMap.set(key, scan)
        } else {
          const prevTs = new Date(prev.completed_at ?? prev.created_at).getTime()
          const curTs  = new Date(scan.completed_at  ?? scan.created_at).getTime()
          if (curTs > prevTs) dayMap.set(key, scan)
        }
      }
      const sorted = [...dayMap.values()].sort(
        (a, b) =>
          new Date(a.completed_at ?? a.created_at).getTime() -
          new Date(b.completed_at ?? b.created_at).getTime()
      )
      return Promise.all(
        sorted.map(async (scan) => ({
          scan,
          findings: await cbomApi.fetchScanFindings(currentOrgId, scan.id).catch(() => null),
        }))
      )
    },
    enabled: sourceIds.length > 0,
    staleTime: 1000 * 60 * 5,
    retry: false,
  })

  const pkgSummaryQuery = useQuery({
    queryKey: ['org-package-summary', currentOrgId],
    queryFn: () => cbomApi.fetchOrgPackageFindings(currentOrgId, { limit: 0 }),
    enabled: !!currentOrgId,
    staleTime: 1000 * 60 * 5,
  })
  const totalPackages = pkgSummaryQuery.data?.summary.total ?? 0
  const totalQuantumSafePackages = pkgSummaryQuery.data?.summary.by_quantum_risk?.['safe'] ?? 0

  const inventoryCountQuery = useQuery({
    queryKey: ['inventory-count', currentOrgId],
    queryFn: () => inventoryApi.list(currentOrgId, { page_size: 1 }),
    enabled: !!currentOrgId,
    staleTime: 1000 * 60 * 5,
  })
  const totalInventories = inventoryCountQuery.data?.total ?? 0

  const pkgTrendQuery = useQuery({
    queryKey: ['pkg-trend', currentOrgId, trendSourceIds],
    queryFn: async () => {
      if (trendSourceIds.length === 0) return []
      const perSourceScans = await Promise.all(
        trendSourceIds.map((sid) =>
          scansApi
            .list(currentOrgId, { source_id: sid, status: 'completed', limit: 100 })
            .then((r) => r.data ?? [])
            .catch(() => [] as ScanJob[])
        )
      )
      const dayMap = new Map<string, ScanJob>()
      for (const scan of perSourceScans.flat()) {
        const day = (scan.completed_at ?? scan.created_at).slice(0, 10)
        const key = `${scan.source_id}::${day}`
        const prev = dayMap.get(key)
        if (!prev) {
          dayMap.set(key, scan)
        } else {
          const prevTs = new Date(prev.completed_at ?? prev.created_at).getTime()
          const curTs  = new Date(scan.completed_at  ?? scan.created_at).getTime()
          if (curTs > prevTs) dayMap.set(key, scan)
        }
      }
      const sorted = [...dayMap.values()].sort(
        (a, b) =>
          new Date(a.completed_at ?? a.created_at).getTime() -
          new Date(b.completed_at ?? b.created_at).getTime()
      )
      return Promise.all(
        sorted.map(async (scan) => ({
          scan,
          findings: await cbomApi.fetchScanPackageFindings(currentOrgId, scan.id, { limit: 0 }).catch(() => null),
        }))
      )
    },
    enabled: sourceIds.length > 0,
    staleTime: 1000 * 60 * 5,
    retry: false,
  })

  const activeSources = sources.filter((s) => s.is_active).length
  const runningScans = scans.filter((s) => s.status === 'running').length
  const recentScans = scans.slice(0, 5)
  const sourceMap = Object.fromEntries(sources.map((s) => [s.id, s.name]))
  const modalityMap = Object.fromEntries(sources.map((s) => [s.id, s.modality]))

  const perScanReports = assetSummaryQuery.data ?? []
  const KNOWN_ASSET_TYPES = ['algorithm', 'certificate', 'key', 'protocol', 'kdf'] as const
  const assetsByType: Record<AssetType | 'others', number> =
    { algorithm: 0, certificate: 0, key: 0, protocol: 0, kdf: 0, others: 0 }
  let totalAssets = 0
  let totalQuantumSafe = 0
  let totalQuantumUnsafe = 0
  for (const report of perScanReports) {
    if (!report) continue
    totalAssets += report.summary.total
    totalQuantumSafe += report.summary.quantum_safe
    totalQuantumUnsafe += report.summary.quantum_unsafe
    for (const [t, count] of Object.entries(report.summary.by_asset_type ?? {}) as [string, number][]) {
      if ((KNOWN_ASSET_TYPES as readonly string[]).includes(t)) {
        assetsByType[t as AssetType] += count ?? 0
      } else {
        assetsByType.others += count ?? 0
      }
    }
  }
  const combinedTotal = totalAssets + totalPackages
  const combinedQuantumSafe = totalQuantumSafe + totalQuantumSafePackages
  const combinedQuantumUnsafe = totalQuantumUnsafe + (totalPackages - totalQuantumSafePackages)
  const quantumSafePct = combinedTotal > 0 ? Math.round((combinedQuantumSafe / combinedTotal) * 100) : 0

  const assetDelta7d = useMemo(() => {
    const data = trendQuery.data
    if (!data || data.length < 2) return null
    const now = Date.now()
    const cutoff = now - 7 * 86_400_000
    const current = data[data.length - 1]?.findings?.summary.total
    const week = data.find(({ scan }) => new Date(scan.completed_at ?? scan.created_at).getTime() <= cutoff)
    if (current == null || !week?.findings) return null
    return current - week.findings.summary.total
  }, [trendQuery.data])

  const isLoading = loadingSources || loadingScans || assetSummaryQuery.isLoading
  const lastCompletedScan = scans.find((s) => s.status === 'completed')

  const tok = chartTokens(isDark)

  const hasOthers = assetsByType.others > 0
  const hasKdf = assetsByType.kdf > 0
  // KDFs only appear once a scan has found one, so the slice is omitted rather
  // than drawn as a permanent zero.
  const assetDoughnutData = {
    labels: [
      'Algorithm', 'Certificate', 'Key', 'Protocol',
      ...(hasKdf ? ['KDF'] : []),
      ...(hasOthers ? ['Others'] : []),
    ],
    datasets: [{
      data: [
        assetsByType.algorithm, assetsByType.certificate, assetsByType.key, assetsByType.protocol,
        ...(hasKdf ? [assetsByType.kdf] : []),
        ...(hasOthers ? [assetsByType.others] : []),
      ],
      backgroundColor: [
        tok.primary, tok.success, tok.warning, tok.neutral,
        ...(hasKdf ? [tok.primaryBg] : []),
        ...(hasOthers ? [tok.textSubtle] : []),
      ],
      borderWidth: isDark ? 0 : 2,
      borderColor: isDark ? 'transparent' : '#ffffff',
    }],
  }

  const ecosystemMap = pkgSummaryQuery.data?.summary.by_ecosystem ?? {}
  const topEcosystems = Object.entries(ecosystemMap).sort((a, b) => b[1] - a[1]).slice(0, 6)
  const ecosystemColors = [tok.primary, tok.success, tok.warning, tok.error, tok.neutral,
    isDark ? 'rgba(148,200,228,0.45)' : 'rgba(37,79,113,0.35)']
  const pkgEcosystemDoughnutData = {
    labels: topEcosystems.map(([k]) => k),
    datasets: [{
      data: topEcosystems.map(([, v]) => v),
      backgroundColor: ecosystemColors,
      borderWidth: isDark ? 0 : 2,
      borderColor: isDark ? 'transparent' : '#ffffff',
    }],
  }

  const doughnutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: 'bottom' as const, labels: { color: tok.textMain, boxWidth: 12, padding: 14, font: { size: 11 } } },
    },
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner />
      </div>
    )
  }

  const apiError = scansError || sourcesError

  return (
    <div className="space-y-6" id="dashboard-page">
      {apiError && (
        <div className="flex items-start gap-3 p-4 bg-error/5 dark:bg-error/10 border border-error/20 dark:border-error/25 rounded-2xl">
          <span className="material-icons text-error text-xl mt-0.5">error_outline</span>
          <div>
            <p className="text-sm font-medium text-error">Cannot reach the qtz-discovery backend</p>
            <p className="text-sm text-error/80 mt-0.5 font-mono">
              {(scansErr as Error)?.message ?? 'Network error'}
            </p>
            <p className="text-xs text-subtle-light dark:text-subtle-dark mt-1">
              Ensure the backend is running at the URL configured in{' '}
              <code className="font-mono">VITE_API_URL</code> and org ID{' '}
              <code className="font-mono">{currentOrgId}</code> exists.
            </p>
          </div>
        </div>
      )}

      {/* Discovery Coverage Banner */}
      <div className="bg-surface-light dark:bg-surface-dark rounded-2xl px-6 py-5 border border-border-light dark:border-border-dark">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-center">
          {/* Left — identity */}
          <div>
            <p className="text-xs font-medium text-subtle-light dark:text-subtle-dark uppercase tracking-widest mb-1">
              Cryptographic Asset Discovery
            </p>
            <h2 className="text-xl font-bold text-text-light dark:text-text-dark tracking-display">
              {orgName ?? 'Organisation'}
            </h2>
            {lastCompletedScan && (
              <p className="text-xs text-subtle-light dark:text-subtle-dark mt-1">
                Last scan: {formatRelativeTime(lastCompletedScan.completed_at ?? lastCompletedScan.started_at)}
              </p>
            )}
            <Link
              to="/sources/new"
              className="inline-flex items-center gap-1.5 mt-3 px-3 py-1.5 bg-primary text-white rounded-lg hover:bg-primary/90 text-xs font-medium"
            >
              <span className="material-icons text-sm">add</span>
              Add Source
            </Link>
          </div>

          {/* Center — total assets + type chips */}
          <div className="text-center">
            <p className="text-5xl font-bold tracking-display text-text-light dark:text-text-dark">
              {sourcesCapped ? `${totalAssets}+` : totalAssets}
            </p>
            <p className="text-xs text-subtle-light dark:text-subtle-dark mt-1 mb-3">crypto assets discovered</p>
            <div className="flex flex-wrap justify-center gap-2">
              {([
                ['algorithm', assetsByType.algorithm, 'text-primary dark:text-secondary', 'bg-primary/10 dark:bg-primary/15'],
                ['certificate', assetsByType.certificate, 'text-success', 'bg-success/10'],
                ['key', assetsByType.key, 'text-warning', 'bg-warning/10'],
                ['protocol', assetsByType.protocol, 'text-subtle-light dark:text-subtle-dark', 'bg-container-light dark:bg-container-dark'],
                ...(hasKdf
                  ? [['kdf', assetsByType.kdf, 'text-primary dark:text-secondary', 'bg-primary/10 dark:bg-primary/15']]
                  : []),
                ...(hasOthers
                  ? [['others', assetsByType.others, 'text-subtle-light dark:text-subtle-dark', 'bg-container-light dark:bg-container-dark']]
                  : []),
              ] as [string, number, string, string][]).map(([type, count, textCls, bgCls]) => (
                <span key={type} className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium ${bgCls} ${textCls}`}>
                  {count} {type}
                </span>
              ))}
            </div>
          </div>

          {/* Right — mini stats */}
          <div className="grid grid-cols-2 gap-3">
            {[
              { label: 'Sources', value: sources.length, icon: 'radar' },
              { label: 'Scans (30d)', value: scans.filter(s => new Date(s.created_at).getTime() > Date.now() - 30 * 86_400_000).length, icon: 'manage_search' },
              { label: 'Packages', value: totalPackages, icon: 'category' },
              { label: 'Inventories', value: totalInventories, icon: 'folder_open' },
            ].map(({ label, value, icon }) => (
              <div key={label} className="bg-container-light dark:bg-container-dark rounded-xl px-3 py-2.5">
                <div className="flex items-center gap-1.5 mb-0.5">
                  <span className="material-icons text-[14px] text-subtle-light dark:text-subtle-dark">{icon}</span>
                  <p className="text-[11px] text-subtle-light dark:text-subtle-dark">{label}</p>
                </div>
                <p className="text-lg font-bold tracking-display text-text-light dark:text-text-dark">{value}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <KpiCard
          label="Total Sources"
          value={sourcesCapped ? `${sources.length}+` : sources.length}
          icon="radar"
          iconBg="bg-primary/10 dark:bg-primary/15"
          color="text-primary dark:text-secondary"
          sub={`${activeSources} active`}
        />
        <KpiCard
          label="Total Inventories"
          value={totalInventories}
          icon="folder_open"
          iconBg="bg-primary/10 dark:bg-primary/15"
          color="text-text-light dark:text-text-dark"
        />
        <KpiCard
          label="Running Scans"
          value={runningScans}
          icon="manage_search"
          iconBg={runningScans > 0 ? 'bg-primary/10 dark:bg-primary/15' : 'bg-container-light dark:bg-container-dark'}
          color={runningScans > 0 ? 'text-primary dark:text-secondary' : 'text-text-light dark:text-text-dark'}
        />
        <KpiCard
          label="Total Assets"
          value={sourcesCapped ? `${totalAssets}+` : totalAssets}
          icon="security"
          iconBg="bg-primary/10 dark:bg-primary/15"
          color="text-text-light dark:text-text-dark"
          sub={`${totalQuantumSafe} quantum-safe`}
          trend={assetDelta7d != null ? { delta: assetDelta7d, label: 'vs 7d ago' } : undefined}
        />
        <KpiCard
          label="Total Packages"
          value={totalPackages}
          icon="inventory_2"
          iconBg="bg-primary/10 dark:bg-primary/15"
          color="text-text-light dark:text-text-dark"
          sub={`${totalQuantumSafePackages} quantum-safe`}
        />
        <KpiCard
          label="Quantum Safe"
          value={`${quantumSafePct}%`}
          icon="verified_user"
          iconBg={quantumSafePct >= 80 ? 'bg-success/10' : quantumSafePct >= 50 ? 'bg-warning/10' : 'bg-error/10'}
          color={quantumSafePct >= 80 ? 'text-success' : quantumSafePct >= 50 ? 'text-warning' : 'text-error'}
          sub={combinedTotal > 0 ? `${combinedQuantumUnsafe} at risk` : 'No data yet'}
        />
      </div>

      {/* Charts row — Scan Jobs Activity | Asset Trends | Assets by Type */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card title="Scan Jobs Activity">
          <ScanActivityChart scans={scans} isDark={isDark} />
        </Card>

        <Card title="Asset Trends">
          <AssetTrendChart
            data={trendQuery.data ?? []}
            isLoading={trendQuery.isLoading}
            isDark={isDark}
            sourceCap={trendSourcesCapped ? TREND_SOURCE_CAP : undefined}
          />
        </Card>

        <Card title="Assets by Type">
          <div className="h-52">
            {Object.values(assetsByType).every((v) => v === 0) ? (
              <EmptyState
                icon="bar_chart"
                title="No assets discovered yet"
                action={
                  <Link to="/scans" className="text-xs text-primary hover:underline">
                    Run a scan to discover assets
                  </Link>
                }
              />
            ) : (
              <Doughnut data={assetDoughnutData} options={doughnutOptions} />
            )}
          </div>
        </Card>
      </div>

      {/* Package Trends + Packages by Ecosystem */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2">
          <Card title="Package Trends">
            <PackageTrendChart
              data={pkgTrendQuery.data ?? []}
              isLoading={pkgTrendQuery.isLoading}
              isDark={isDark}
              sourceCap={trendSourcesCapped ? TREND_SOURCE_CAP : undefined}
            />
          </Card>
        </div>

        <Card title="Packages by Ecosystem">
          <div className="h-52">
            {topEcosystems.length === 0 ? (
              <EmptyState
                icon="bar_chart"
                title="No packages discovered yet"
                action={
                  <Link to="/scans" className="text-xs text-primary hover:underline">
                    Run a scan to discover packages
                  </Link>
                }
              />
            ) : (
              <Doughnut data={pkgEcosystemDoughnutData} options={doughnutOptions} />
            )}
          </div>
        </Card>
      </div>

      {/* Recent Scan Jobs */}
      <Card title="Recent Scan Jobs">
        <div className="-mx-5 -mb-5">
          <div className="flex items-center justify-between px-5 pb-3">
            <span />
            <Link to="/scans" className="text-sm text-primary dark:text-secondary hover:underline">
              View all
            </Link>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" id="recent-scans-table">
              <thead className="bg-container-light dark:bg-container-dark">
                <tr>
                  <th className="px-5 py-3 text-left label-sm text-subtle-light dark:text-subtle-dark">Scan ID</th>
                  <th className="px-5 py-3 text-left label-sm text-subtle-light dark:text-subtle-dark">Source</th>
                  <th className="px-5 py-3 text-left label-sm text-subtle-light dark:text-subtle-dark">Modality</th>
                  <th className="px-5 py-3 text-left label-sm text-subtle-light dark:text-subtle-dark">Status</th>
                  <th className="px-5 py-3 text-left label-sm text-subtle-light dark:text-subtle-dark">Progress</th>
                  <th className="px-5 py-3 text-left label-sm text-subtle-light dark:text-subtle-dark">Trigger</th>
                  <th className="px-5 py-3 text-left label-sm text-subtle-light dark:text-subtle-dark">Started</th>
                </tr>
              </thead>
              <tbody>
                {recentScans.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-5 py-8 text-center text-subtle-light dark:text-subtle-dark">
                      No scans yet.{' '}
                      <Link to="/sources" className="text-primary hover:underline">Add a source</Link>{' '}
                      to get started.
                    </td>
                  </tr>
                ) : (
                  recentScans.map((scan, i) => (
                    <tr
                      key={scan.id}
                      className={`hover:bg-container-light dark:hover:bg-container-dark transition-colors ${
                        i % 2 === 1 ? 'bg-background-light dark:bg-background-dark' : ''
                      }`}
                    >
                      <td className="px-5 py-3">
                        <Link to={`/scans/${scan.id}`} className="text-primary dark:text-secondary hover:underline font-mono text-xs">
                          {scan.id.slice(0, 8)}…
                        </Link>
                      </td>
                      <td className="px-5 py-3">
                        <Link
                          to={`/sources/${scan.source_id}`}
                          className="text-text-light dark:text-text-dark hover:text-primary dark:hover:text-secondary hover:underline truncate max-w-[160px] block text-xs"
                          title={sourceMap[scan.source_id]}
                        >
                          {sourceMap[scan.source_id] ?? scan.source_id.slice(0, 8) + '…'}
                        </Link>
                      </td>
                      <td className="px-5 py-3">
                        {modalityMap[scan.source_id]
                          ? <ModalityBadge modality={modalityMap[scan.source_id] as Modality} />
                          : <span className="text-subtle-light dark:text-subtle-dark">—</span>}
                      </td>
                      <td className="px-5 py-3">
                        <ScanStatusBadge status={scan.status} />
                      </td>
                      <td className="px-5 py-3 w-40">
                        <ProgressBar value={scan.progress} animated={scan.status === 'running'} size="sm" showLabel />
                      </td>
                      <td className="px-5 py-3 text-subtle-light dark:text-subtle-dark capitalize">
                        {scan.trigger_type}
                      </td>
                      <td className="px-5 py-3 text-subtle-light dark:text-subtle-dark">
                        {formatRelativeTime(scan.started_at)}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </Card>
    </div>
  )
}
